// Import Home actions for closing the loyalty page
import { toggleMobileCart, setState } from 'containers/Home/actions';
import { defaultHomePropsState } from 'constants/common';
import { mutate } from 'swr';
import { API_URL, LOYALTY_API_URL } from 'configuration/urls';
import { LOYALTY_DEFAULTS } from './constants';

// Action Types
export const FETCH_LOYALTY_DETAILS_REQUEST = 'FETCH_LOYALTY_DETAILS_REQUEST';
export const FETCH_LOYALTY_DETAILS_SUCCESS = 'FETCH_LOYALTY_DETAILS_SUCCESS';
export const FETCH_LOYALTY_DETAILS_FAILURE = 'FETCH_LOYALTY_DETAILS_FAILURE';
export const RESET_LOYALTY_DETAILS = 'RESET_LOYALTY_DETAILS';

// Store Settings Action Types
export const FETCH_STORE_LOYALTY_SETTINGS_REQUEST =
  'FETCH_STORE_LOYALTY_SETTINGS_REQUEST';
export const FETCH_STORE_LOYALTY_SETTINGS_SUCCESS =
  'FETCH_STORE_LOYALTY_SETTINGS_SUCCESS';
export const FETCH_STORE_LOYALTY_SETTINGS_FAILURE =
  'FETCH_STORE_LOYALTY_SETTINGS_FAILURE';

// Pre-Signup Data Action Types (replaces separate earnings, promotions, redemptions)
export const FETCH_PRE_SIGNUP_DATA_REQUEST = 'FETCH_PRE_SIGNUP_DATA_REQUEST';
export const FETCH_PRE_SIGNUP_DATA_SUCCESS = 'FETCH_PRE_SIGNUP_DATA_SUCCESS';
export const FETCH_PRE_SIGNUP_DATA_FAILURE = 'FETCH_PRE_SIGNUP_DATA_FAILURE';

// User Points Action Types
export const FETCH_USER_POINTS_REQUEST = 'FETCH_USER_POINTS_REQUEST';
export const FETCH_USER_POINTS_SUCCESS = 'FETCH_USER_POINTS_SUCCESS';
export const FETCH_USER_POINTS_FAILURE = 'FETCH_USER_POINTS_FAILURE';

// Earnings Action Types
export const FETCH_EARNINGS_REQUEST = 'FETCH_EARNINGS_REQUEST';
export const FETCH_EARNINGS_SUCCESS = 'FETCH_EARNINGS_SUCCESS';
export const FETCH_EARNINGS_FAILURE = 'FETCH_EARNINGS_FAILURE';

// Promotions Action Types
export const FETCH_PROMOTIONS_REQUEST = 'FETCH_PROMOTIONS_REQUEST';
export const FETCH_PROMOTIONS_SUCCESS = 'FETCH_PROMOTIONS_SUCCESS';
export const FETCH_PROMOTIONS_FAILURE = 'FETCH_PROMOTIONS_FAILURE';

// Redemptions Action Types
export const FETCH_REDEMPTIONS_REQUEST = 'FETCH_REDEMPTIONS_REQUEST';
export const FETCH_REDEMPTIONS_SUCCESS = 'FETCH_REDEMPTIONS_SUCCESS';
export const FETCH_REDEMPTIONS_FAILURE = 'FETCH_REDEMPTIONS_FAILURE';

// Available Redemptions Action Types
export const FETCH_AVAILABLE_REDEMPTIONS_REQUEST =
  'FETCH_AVAILABLE_REDEMPTIONS_REQUEST';
export const FETCH_AVAILABLE_REDEMPTIONS_SUCCESS =
  'FETCH_AVAILABLE_REDEMPTIONS_SUCCESS';
export const FETCH_AVAILABLE_REDEMPTIONS_FAILURE =
  'FETCH_AVAILABLE_REDEMPTIONS_FAILURE';

export const FETCH_LOYALTY_HISTORY_REQUEST = 'FETCH_LOYALTY_HISTORY_REQUEST';
export const FETCH_LOYALTY_HISTORY_SUCCESS = 'FETCH_LOYALTY_HISTORY_SUCCESS';
export const FETCH_LOYALTY_HISTORY_FAILURE = 'FETCH_LOYALTY_HISTORY_FAILURE';

export const CREATE_LOYALTY_SIGNUP_REQUEST = 'CREATE_LOYALTY_SIGNUP_REQUEST';
export const CREATE_LOYALTY_SIGNUP_SUCCESS = 'CREATE_LOYALTY_SIGNUP_SUCCESS';
export const CREATE_LOYALTY_SIGNUP_FAILURE = 'CREATE_LOYALTY_SIGNUP_FAILURE';
// Loyalty Voucher Action Type
export const STORE_LOYALTY_VOUCHER = 'STORE_LOYALTY_VOUCHER';
export const SHOW_ENROLLMENT_FORM = 'SHOW_ENROLLMENT_FORM';
// =======================================
// SWR Fetcher Functions
// =======================================

/**
 * SWR fetcher function for user points
 * @param {string} url - The API URL to fetch user points
 * @returns {Promise<number>} - The points value
 */
export const fetchUserPointsSWR = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch user points: ${response.statusText}`);
  }
  const data = await response.json();
  if (
    data &&
    data.success === true &&
    data.data &&
    data.data.points !== undefined
  ) {
    return data.data.points || 0;
  } else {
    console.warn('Unexpected points data structure:', data);
    return 0;
  }
};

/**
 * SWR fetcher function for loyalty details
 * @param {string} url - The API URL to fetch loyalty details
 * @returns {Promise<Object|null>} - The loyalty details or null
 */
export const fetchLoyaltyDetailsSWR = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch loyalty details: ${response.statusText}`);
  }
  const data = await response.json();
  if (data && data.success === true && data.data) {
    return data.data || null;
  }
  return null;
};

/**
 * SWR fetcher function for loyalty details
 * @param {string} url - The API URL to fetch loyalty details
 * @returns {Promise<Object|null>} - The loyalty details or null
 */
export const fetchLoyaltyDetailsForHome = async (url) => {
  const response = await fetch(url);
  if (response.status === 404) {
    return {
      isEnrolled: false
    };
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch loyalty details: ${response.statusText}`);
  }
  const data = await response.json();
  if (data && data.success === true && data.data) {
    return data.data || null;
  }
  return null;
};

/**
 * SWR fetcher function for store loyalty settings
 * @param {string} url - The API URL to fetch store loyalty settings
 * @returns {Promise<Object|null>} - The store loyalty settings or null
 */
export const fetchStoreLoyaltySettingsSWR = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch store loyalty settings: ${response.statusText}`
    );
  }
  const data = await response.json();
  console.log('🔍 Store loyalty settings data:', data);
  if (data && data.success === true && data.data) {
    return data.data;
  }
  return null;
};

/**
 * SWR fetcher function for available redemptions
 * @param {string} url - The API URL to fetch available redemptions
 * @returns {Promise<Array>} - Array of available redemptions
 */
export const fetchAvailableRedemptionsSWR = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch available redemptions: ${response.statusText}`
    );
  }
  const data = await response.json();
  if (data && data.success === true && data.data) {
    return data.data || [];
  } else {
    console.warn('Unexpected available redemptions data structure:', data);
    return [];
  }
};

/**
 * SWR fetcher function for loyalty history
 * @param {string} url - The API URL to fetch loyalty history
 * @returns {Promise<Array>} - Array of loyalty history items
 */
export const fetchLoyaltyHistorySWR = async (url) => {
  // Parse the URL to get the parameters
  const urlObj = new URL(url);
  const offset = urlObj.searchParams.get('offset') || 0;
  const limit = urlObj.searchParams.get('limit') || 10;
  const restaurantId = urlObj.searchParams.get('restaurantId') || '';
  const storeId = urlObj.searchParams.get('storeId') || null;
  const userId = urlObj.searchParams.get('userId') || '';
  const startDate = urlObj.searchParams.get('startDate') || null;
  const endDate = urlObj.searchParams.get('endDate') || null;

  // Create the request body
  const requestBody = {
    userId,
    restaurantId,
    offset: parseInt(offset),
    limit: parseInt(limit),
  };

  // Add optional parameters only if they exist
  if (storeId) {
    requestBody.storeId = storeId;
  }
  
  if (startDate) {
    requestBody.startDate = startDate;
  }
  
  if (endDate) {
    requestBody.endDate = endDate;
  }

  // Make the POST request
  const response = await fetch(`${LOYALTY_API_URL}loyaltyHistory`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch loyalty history: ${response.statusText}`);
  }

  const data = await response.json();
  if (data && data.success === true && data.data) {
    return data.data || [];
  } else {
    console.warn('Unexpected loyalty history data structure:', data);
    return [];
  }
};

/**
 * Gets the URL for fetching user points with SWR
 * @param {string} userId - The user ID
 * @param {string} storeId - The store ID
 * @param {string} restaurantId - The restaurant ID
 * @param {number} level - The loyalty level
 * @returns {string|null} - The URL or null if parameters are missing
 */
export const getUserPointsUrl = (userId, storeId, restaurantId, level = LOYALTY_DEFAULTS.LEVEL) => {
  if (!userId || !storeId || !restaurantId) return null;
  return `${LOYALTY_API_URL}loyaltyGetPoints?userId=${userId}&storeId=${storeId}&restaurantId=${restaurantId}&level=${level}`;
};

/**
 * Gets the URL for fetching loyalty details with SWR
 * @param {string} userId - The user ID
 * @param {string} settingId - The loyalty setting ID
 * @returns {string|null} - The URL or null if parameters are missing
 */
export const getLoyaltyDetailsUrl = (userId, settingId) => {
  if (!userId || !settingId) return null;
  return `${LOYALTY_API_URL}loyaltyAccount?userId=${userId}&settingId=${settingId}`;
};

/**
 * Gets the URL for fetching store loyalty settings with SWR
 * @param {string} restaurantId - The restaurant ID
 * @param {string} storeId - The store ID
 * @param {number} level - The loyalty level
 * @returns {string|null} - The URL or null if parameters are missing
 */
export const getStoreLoyaltySettingsUrl = (
  restaurantId,
  storeId,
  level = 0
) => {
  if (!restaurantId) return null;
  let url = `${LOYALTY_API_URL}loyaltySettings?restaurantId=${restaurantId}`;
  if (storeId) {
    url += `&storeId=${storeId}`;
  }
  url += `&level=${level}`;
  return url;
};

/**
 * Gets the URL for fetching available redemptions with SWR
 * @param {string} userId - The user ID
 * @param {string} storeId - The store ID
 * @param {string} restaurantId - The restaurant ID
 * @param {number} level - The loyalty level
 * @returns {string|null} - The URL or null if parameters are missing
 */
export const getAvailableRedemptionsUrl = (
  userId,
  storeId,
  restaurantId,
  level = 0
) => {
  if (!userId || !storeId || !restaurantId) return null;
  return `${LOYALTY_API_URL}loyaltyGetRedemptions?userId=${userId}&storeId=${storeId}&restaurantId=${restaurantId}&level=${level}&limit=50`;
};

/**
 * Gets the URL for fetching promotions with SWR
 * @param {string} settingId - The setting ID
 * @param {number} level - The loyalty level
 * @returns {string|null} - The URL or null if parameters are missing
 */
export const getPromotionsUrl = (settingId, level) => {
  if (!settingId) return null;
  return `${LOYALTY_API_URL}loyaltyPromotions?settingId=${settingId}&level=${level}&limit=100&offset=0`;
};

/**
 * Gets the URL for fetching loyalty history with SWR
 * @param {string} userId - The user ID (required)
 * @param {string} storeId - The store ID (optional)
 * @param {string} restaurantId - The restaurant ID (required)
 * @param {number} offset - The offset for pagination (defaults to 0)
 * @param {number} limit - The limit of records to fetch (defaults to 10)
 * @param {string|null} startDate - The start date filter (optional)
 * @param {string|null} endDate - The end date filter (optional)
 * @returns {string|null} - The URL for SWR or null if required parameters are missing
 */
export const getLoyaltyHistoryUrl = (
  userId,
  storeId,
  restaurantId,
  offset = 0,
  limit = 10,
  startDate = null,
  endDate = null
) => {
  if (!userId || !restaurantId) return null;

  // Build the URL with only the parameters the backend expects
  let url = `${LOYALTY_API_URL}loyaltyHistory?userId=${userId}&restaurantId=${restaurantId}&offset=${offset}&limit=${limit}`;

  // Add optional storeId if provided
  if (storeId) {
    url += `&storeId=${storeId}`;
  }

  // Add optional date filters if provided
  if (startDate) {
    url += `&startDate=${startDate}`;
  }
  
  if (endDate) {
    url += `&endDate=${endDate}`;
  }

  return url;
};

/**
 * SWR fetcher function for promotions
 * @param {string} url - The URL to fetch
 * @returns {Promise<Array|null>} - The promotions or null
 */
export const fetchPromotionsSWR = async (url) => {
  if (!url) return null;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch promotions: ${response.status}`);
    }
    const data = await response.json();
    return data?.data || [];
  } catch (error) {
    throw error;
  }
};

/**
 * SWR fetcher function for presignup data
 * @param {string} url - The API URL to fetch presignup data
 * @returns {Promise<Object|null>} - The presignup data or null
 */
export const fetchPreSignupDataSWR = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch presignup data: ${response.statusText}`);
  }
  const data = await response.json();
  if (data && data.success === true && data.data) {
    return data.data || null;
  }
  return null;
};

/**
 * Gets the URL for fetching presignup data with SWR
 * @param {string} settingId - The loyalty setting ID (from storeLoyaltySettings.id)
 * @returns {string|null} - The URL or null if settingId is missing
 */
export const getPreSignupDataUrl = (settingId) => {
  if (!settingId) return null;
  return `${LOYALTY_API_URL}loyaltyPreSignup?settingId=${settingId}`;
};

// Action Creators
export const fetchLoyaltyDetailsRequest = () => ({
  type: FETCH_LOYALTY_DETAILS_REQUEST,
});

export const fetchLoyaltyDetailsSuccess = (loyaltyDetails) => ({
  type: FETCH_LOYALTY_DETAILS_SUCCESS,
  payload: loyaltyDetails,
});

export const fetchLoyaltyDetailsFailure = (error) => ({
  type: FETCH_LOYALTY_DETAILS_FAILURE,
  payload: error,
});

export const resetLoyaltyDetails = () => ({
  type: RESET_LOYALTY_DETAILS,
});

// Store Settings Action Creators
export const fetchStoreLoyaltySettingsRequest = () => ({
  type: FETCH_STORE_LOYALTY_SETTINGS_REQUEST,
});

export const fetchStoreLoyaltySettingsSuccess = (settings) => ({
  type: FETCH_STORE_LOYALTY_SETTINGS_SUCCESS,
  payload: settings,
});

export const fetchStoreLoyaltySettingsFailure = (error) => ({
  type: FETCH_STORE_LOYALTY_SETTINGS_FAILURE,
  payload: error,
});

// User Points Action Creators
export const fetchUserPointsRequest = () => ({
  type: FETCH_USER_POINTS_REQUEST,
});

export const fetchUserPointsSuccess = (points) => ({
  type: FETCH_USER_POINTS_SUCCESS,
  payload: points,
});

export const fetchUserPointsFailure = (error) => ({
  type: FETCH_USER_POINTS_FAILURE,
  payload: error,
});

// Pre-Signup Data Action Creators
export const fetchPreSignupDataRequest = () => ({
  type: FETCH_PRE_SIGNUP_DATA_REQUEST,
});

export const fetchPreSignupDataSuccess = (data) => ({
  type: FETCH_PRE_SIGNUP_DATA_SUCCESS,
  payload: data,
});

export const fetchPreSignupDataFailure = (error) => ({
  type: FETCH_PRE_SIGNUP_DATA_FAILURE,
  payload: error,
});

// Earnings Action Creators
export const fetchEarningsRequest = () => ({
  type: FETCH_EARNINGS_REQUEST,
});

export const fetchEarningsSuccess = (earnings) => ({
  type: FETCH_EARNINGS_SUCCESS,
  payload: earnings,
});

export const fetchEarningsFailure = (error) => ({
  type: FETCH_EARNINGS_FAILURE,
  payload: error,
});

// Promotions Action Creators
export const fetchPromotionsRequest = () => ({
  type: FETCH_PROMOTIONS_REQUEST,
});

export const fetchPromotionsSuccess = (promotions) => ({
  type: FETCH_PROMOTIONS_SUCCESS,
  payload: promotions,
});

export const fetchPromotionsFailure = (error) => ({
  type: FETCH_PROMOTIONS_FAILURE,
  payload: error,
});

// Redemptions Action Creators
export const fetchRedemptionsRequest = () => ({
  type: FETCH_REDEMPTIONS_REQUEST,
});

export const fetchRedemptionsSuccess = (redemptions) => ({
  type: FETCH_REDEMPTIONS_SUCCESS,
  payload: redemptions,
});

export const fetchRedemptionsFailure = (error) => ({
  type: FETCH_REDEMPTIONS_FAILURE,
  payload: error,
});

// Available Redemptions Action Creators
export const fetchAvailableRedemptionsRequest = () => ({
  type: FETCH_AVAILABLE_REDEMPTIONS_REQUEST,
});

export const fetchAvailableRedemptionsSuccess = (availableRedemptions) => ({
  type: FETCH_AVAILABLE_REDEMPTIONS_SUCCESS,
  payload: availableRedemptions,
});

export const fetchAvailableRedemptionsFailure = (error) => ({
  type: FETCH_AVAILABLE_REDEMPTIONS_FAILURE,
  payload: error,
});

// Loyalty History Action Creators
export const fetchLoyaltyHistoryRequest = () => ({
  type: FETCH_LOYALTY_HISTORY_REQUEST,
});

export const fetchLoyaltyHistorySuccess = (history) => ({
  type: FETCH_LOYALTY_HISTORY_SUCCESS,
  payload: history,
});

export const fetchLoyaltyHistoryFailure = (error) => ({
  type: FETCH_LOYALTY_HISTORY_FAILURE,
  payload: error,
});

// Loyalty History Action Creators
export const createLoyaltySignupRequest = () => ({
  type: CREATE_LOYALTY_SIGNUP_REQUEST,
});

export const createLoyaltySignupSuccess = (user) => ({
  type: CREATE_LOYALTY_SIGNUP_SUCCESS,
  payload: user,
});

export const createLoyaltySignupFailure = (error) => ({
  type: CREATE_LOYALTY_SIGNUP_FAILURE,
  payload: error,
});

// Loyalty Voucher Action Creator
export const storeLoyaltyVoucher = (voucherCode) => ({
  type: STORE_LOYALTY_VOUCHER,
  payload: voucherCode,
});

export const showEnrollmentForm = (value) => ({
  type: SHOW_ENROLLMENT_FORM,
  payload: value,
});

// Thunk action to fetch earnings settings
export const fetchEarnings = (settingId) => async (dispatch) => {
  try {
    if (!settingId) {
      console.error('SettingId is required to fetch earnings');
      return;
    }

    dispatch(fetchEarningsRequest());

    // Fetch earnings data
    const response = await fetch(
      `${LOYALTY_API_URL}loyaltyEarnings?settingId=${settingId}&level=0`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch earnings: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.success === true && data.data?.data?.earnings) {
      dispatch(fetchEarningsSuccess(data.data.data.earnings));
      return data.data.data.earnings;
    } else {
      throw new Error('Invalid response format or no earnings found');
    }
  } catch (error) {
    console.error('Error fetching earnings:', error);
    dispatch(fetchEarningsFailure(error.message));
    return null;
  }
};

// Thunk action to fetch promotions
export const fetchPromotions =
  (settingId, limit = 10, offset = 0) =>
  async (dispatch) => {
    try {
      if (!settingId) {
        console.error('SettingId is required to fetch promotions');
        return;
      }

      dispatch(fetchPromotionsRequest());

      // Fetch promotions data
      const response = await fetch(
        `${LOYALTY_API_URL}loyaltyPromotions?settingId=${settingId}&level=0&limit=${limit}&offset=${offset}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch promotions: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success === true && data.data?.promotions) {
        dispatch(fetchPromotionsSuccess(data.data.promotions));
        return data.data.promotions;
      } else {
        throw new Error('Invalid response format or no promotions found');
      }
    } catch (error) {
      console.error('Error fetching promotions:', error);
      dispatch(fetchPromotionsFailure(error.message));
      return null;
    }
  };

// Thunk action to fetch redemptions
export const fetchRedemptions = (storeId, restaurantId) => async (dispatch) => {
  try {
    if (!storeId || !restaurantId) {
      console.error(
        'StoreId and RestaurantId are required to fetch redemptions'
      );
      return;
    }

    dispatch(fetchRedemptionsRequest());

    // Fetch redemptions data
    const response = await fetch(
      `${LOYALTY_API_URL}loyaltyRedemptions?storeId=${storeId}&restaurantId=${restaurantId}&level=0&offset=0&limit=100`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch redemptions: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.success === true && data.data?.data?.redemptions) {
      dispatch(fetchRedemptionsSuccess(data.data.data.redemptions));
      return data.data.data.redemptions;
    } else {
      throw new Error('Invalid response format or no redemptions found');
    }
  } catch (error) {
    console.error('Error fetching redemptions:', error);
    dispatch(fetchRedemptionsFailure(error.message));
    return null;
  }
};

// Thunk action to fetch available redemptions for a user
export const fetchAvailableRedemptions =
  (userId, storeId, restaurantId, level = 0) =>
  async (dispatch) => {
    try {
      if (!userId || !storeId || !restaurantId) {
        console.error(
          'UserId, storeId, and restaurantId are required to fetch available redemptions'
        );
        return;
      }

      dispatch(fetchAvailableRedemptionsRequest());

      // Make API call to get available redemptions
      const response = await fetch(
        `${LOYALTY_API_URL}loyaltyGetRedemptions?userId=${userId}&storeId=${storeId}&restaurantId=${restaurantId}&level=${level}&limit=50`
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch available redemptions: ${response.statusText}`
        );
      }

      const data = await response.json();

      // Update state with available redemptions
      if (data && data.success === true && data.data?.data?.redemptions) {
        dispatch(
          fetchAvailableRedemptionsSuccess(data.data.data.redemptions || [])
        );
        return data.data.data.redemptions || [];
      } else if (data && data.success === true && data.data) {
        dispatch(fetchAvailableRedemptionsSuccess(data.data || []));
        return data.data || [];
      } else {
        // Handle case where response is valid but doesn't contain expected data
        console.warn('Unexpected available redemptions data structure:', data);
        dispatch(fetchAvailableRedemptionsSuccess([]));
        return [];
      }
    } catch (error) {
      console.error('Error fetching available redemptions:', error);
      dispatch(fetchAvailableRedemptionsFailure(error.message));
      return null;
    }
  };

/**
 * Handle redemption action
 * @param {Object} params - Redemption parameters
 * @param {number} params.redemptionId - The ID of the reward to redeem
 * @param {string} params.userId - The user ID
 * @param {string} params.storeId - The store ID
 * @param {string} params.restaurantId - The restaurant ID
 * @param {number} params.level - The loyalty level (defaults to 0)
 * @param {function} params.dispatch - Redux dispatch function
 * @returns {Promise<Object|null>} - The response data or null on error
 */
export const handleRedemption = async ({
  redemptionId,
  userId,
  storeId,
  restaurantId,
  level = 0,
  dispatch,
}) => {
  try {
    if (!redemptionId || !userId || !storeId || !restaurantId) {
      console.error(
        'RedemptionId, userId, storeId, and restaurantId are required for redemption'
      );
      return null;
    }

    // Create the request body
    const requestBody = {
      redemptionId,
      level: level,
      restaurantId,
      storeId,
      userId,
    };

    // Make the POST request
    const response = await fetch(`${LOYALTY_API_URL}loyaltyRedeemPoints`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Failed to redeem reward: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Redemption response:', data);

    // Check if redemption was successful
    if (data?.success === true) {
      // Check if voucher code exists and dispatch action to store it
      if (data?.data?.voucherCode && data?.data?.voucherInfo && dispatch) {
        const voucherCode = data.data.voucherCode;
        const voucherInfo = data.data.voucherInfo;

        // Ensure voucherInfo is complete
        if (!voucherInfo || typeof voucherInfo !== 'object') {
          return null;
        }

        // Create a complete voucher object with loyalty flag
        const loyaltyVoucherData = {
          voucherCode,
          voucherInfo,
          isLoyaltyVoucher: true,
          type: voucherInfo.type,
          discount: voucherInfo.discount,
          redemptionId: redemptionId,
          // Add other necessary fields from voucherInfo
        };

        dispatch(storeLoyaltyVoucher(loyaltyVoucherData));

        // Close the loyalty page if successful and we have a voucher code
        dispatch(toggleMobileCart(true));
        dispatch(setState({ ...defaultHomePropsState }));
      }

      return data;
    } else {
      console.error('Redemption failed:', data);
      return null;
    }
  } catch (error) {
    console.error('Error redeeming reward:', error);
    return null;
  }
};

/**
 * Handle promotion redemption action
 * @param {Object} params - Promotion redemption parameters
 * @param {number} params.promotionId - The ID of the promotion to redeem
 * @param {string} params.userId - The user ID
 * @param {string} params.storeId - The store ID
 * @param {string} params.restaurantId - The restaurant ID
 * @param {number} params.level - The loyalty level (defaults to 0)
 * @returns {Promise<Object|null>} - The response data or null on error
 */
export const handlePromotionRedemption = async ({
  promotionId,
  userId,
  storeId,
  restaurantId,
  level = 0,
}) => {
  try {
    if (!promotionId || !userId || !storeId || !restaurantId) {
      console.error(
        'PromotionId, userId, storeId, and restaurantId are required for promotion redemption'
      );
      return null;
    }

    // Create the request body
    const requestBody = {
      id: parseInt(promotionId),
      level,
      restaurantId,
      storeId,
      userId,
    };

    // For now, just log the request and return a mock response
    console.log('Would send promotion redemption with payload:', requestBody);

    // TO-DO: Replace this with an actual API call
    // Example implementation once API is ready:
    /*
    const response = await fetch(
      `${LOYALTY_API_URL}loyaltyPromotionRedemption`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to redeem promotion: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Promotion redemption response:', data);
    
    return data;
    */

    // Mock response until API is ready
    alert('Promotion redemption feature coming soon!');
    return { success: false, message: 'API not implemented yet' };
  } catch (error) {
    console.error('Error redeeming promotion:', error);
    return null;
  }
};

export const createLoyaltyUserSignup =
  ({
    firstName,
    lastName,
    email,
    storeId,
    restaurantId,
    mobileNumber,
    userId,
    enrollmentFields,
    settingId,
    referralId,
    level = LOYALTY_DEFAULTS.LEVEL,
  }) =>
  async (dispatch) => {
    try {
      if (!settingId) {
        console.error('settingId is required to create loyalty user');
        return;
      }

      dispatch(createLoyaltySignupRequest());
      const requestBody = {
        email,
        firstName,
        lastName,
        level,
        enrollmentFields: enrollmentFields || {},
        mobileNumber,
        restaurantId,
        storeId,
        userId,
        settingId,
      };

      // Only include referralId if it's a non-empty string
      if (
        referralId &&
        typeof referralId === 'string' &&
        referralId.trim() !== ''
      ) {
        requestBody.referralId = referralId;
      }

      // loyalty user signup
      const response = await fetch(`${LOYALTY_API_URL}loyaltyAccount`, {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to create loyalty user: ${response.statusText}`
        );
      }

      const data = await response.json();

      if (data.success === true && data.data) {
        dispatch(createLoyaltySignupSuccess(data.data));
        dispatch(showEnrollmentForm(false));
        return data.data;
      } else {
        dispatch(showEnrollmentForm(false));
        console.error('Failed to create loyalty user:', data);
        return null;
      }
    } catch (error) {
      console.error('Failed to create loyalty user:', error);
      dispatch(createLoyaltySignupFailure(error.message));
      dispatch(showEnrollmentForm(false));
      return null;
    }
  };

/**
 * Update loyalty account
 * @param {Object} params - Update parameters
 * @param {string} params.userId - The user ID
 * @param {string} params.settingId - The loyalty setting ID
 * @param {Object} params.updateData - The data to update
 * @returns {Promise<Object|null>} - The updated account data or null on error
 */
export const updateLoyaltyAccount = async ({
  userId,
  settingId,
  updateData,
}) => {
  try {
    if (!userId || !settingId) {
      console.error(
        'UserId and settingId are required to update loyalty account'
      );
      return null;
    }

    const requestBody = {
      userId,
      settingId,
      ...updateData,
    };

    const response = await fetch(`${LOYALTY_API_URL}loyaltyAccount`, {
      method: 'PUT',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to update loyalty account: ${response.statusText}`
      );
    }

    const data = await response.json();

    if (data.success === true && data.data) {
      return data.data;
    } else {
      console.error('Failed to update loyalty account:', data);
      return null;
    }
  } catch (error) {
    console.error('Error updating loyalty account:', error);
    return null;
  }
};

/**
 * Delete loyalty account
 * @param {string} accountId - The account ID to delete
 * @returns {Promise<boolean>} - True if successful, false otherwise
 */
export const deleteLoyaltyAccount = async (accountId) => {
  try {
    if (!accountId) {
      console.error('AccountId is required to delete loyalty account');
      return false;
    }

    const response = await fetch(`${LOYALTY_API_URL}loyaltyAccount`, {
      method: 'DELETE',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ accountId }),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to delete loyalty account: ${response.statusText}`
      );
    }

    const data = await response.json();

    if (data.success === true) {
      return true;
    } else {
      console.error('Failed to delete loyalty account:', data);
      return false;
    }
  } catch (error) {
    console.error('Error deleting loyalty account:', error);
    return false;
  }
};

export const assignPointsToUserScan =
  ({ orderId, mobileNumber, restaurantId, userId, storeId }) =>
  async (dispatch, getState) => {
    try {
      const { storeLoyaltySettings } = getState().loyaltyReducer;
      if (!storeId || !restaurantId) {
        console.error(
          'StoreId and RestaurantId are required to create loyalty user'
        );
        return;
      }

      const requestBody = {
        orderId,
        mobileNumber,
        restaurantId,
        userId,
        storeId,
      };

      const response = await fetch(`${LOYALTY_API_URL}loyaltyScan`, {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to assign loyalty points to user check what's happening: ${response.statusText}`
        );
      }

      const data = await response.json();

      if (data.success === true) {
        const key = getUserPointsUrl(
          userId,
          storeId,
          restaurantId,
          storeLoyaltySettings?.level || 0
        );
        mutate(key);
        
        return data.data;
      } else {
        console.error('Failed to create loyalty user');
        return null;
      }
    } catch (error) {
      console.error('Failed to create loyalty user:', error);
      return null;
    }
  };

/**
 * Search for addresses based on input text
 * @param {Object} params - Search parameters
 * @param {string} params.input - Address text to search
 * @param {string} params.types - Type of place (e.g., "address", "geocode")
 * @param {string} params.country - Country code (e.g., "au" for Australia)
 * @param {string} params.requestFrom - Source of request (e.g., "website", "POS")
 * @param {string} params.restaurantId - Restaurant identifier
 * @param {string} params.storeId - Store identifier
 * @param {string} params.cloudFunctionsUrl - Base URL for cloud functions
 * @returns {Promise<Object|null>} - The search results or null on error
 */
export const searchAddresses = async ({
  input,
  types,
  country,
  requestFrom,
  restaurantId,
  storeId,
  cloudFunctionsUrl,
}) => {
  try {
    if (!input || input.length < 3 || !cloudFunctionsUrl) {
      return null;
    }

    const response = await fetch(`${cloudFunctionsUrl}/addressSearch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        input,
        types,
        country,
        requestFrom,
        restaurantId,
        storeId,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch address suggestions');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching address suggestions:', error);
    return null;
  }
};

/**
 * Get address details by place ID
 * @param {Object} params - Request parameters
 * @param {string} params.placeId - Google Place ID
 * @param {string} params.requestFrom - Source of request (e.g., "website", "POS")
 * @param {string} params.restaurantId - Restaurant identifier
 * @param {string} params.storeId - Store identifier
 * @param {string} params.cloudFunctionsUrl - Base URL for cloud functions
 * @returns {Promise<Object|null>} - The address details or null on error
 */
export const getAddressByPlaceId = async ({
  placeId,
  requestFrom,
  restaurantId,
  storeId,
  cloudFunctionsUrl,
}) => {
  try {
    if (!placeId || !cloudFunctionsUrl) {
      return null;
    }

    const response = await fetch(`${cloudFunctionsUrl}/getAddressByPlaceId`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        placeId,
        requestFrom,
        restaurantId,
        storeId,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch address details');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching address details:', error);
    return null;
  }
};
